import { bool, func, object } from 'prop-types';
import { propTypes } from '../../util/types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { generateIntercomUserHash } from '../../ducks/user.duck';
import { useReactToChange } from '../../util/hooks';
import { CONSENT_CATEGORIES, hasConsent } from '../../util/cookieConsent';

// Intercom
import Intercom, { update, shutdown } from '@intercom/messenger-js-sdk';

/**
 * Intercom support messenger.
 *
 * Gated on the visitor's consent. Intercom sets its own cookies and, for a
 * signed-in user, is handed their id, name and email, so it is a third party
 * in the plain sense and does not load until the Functional category has been
 * granted. Before the answer is known it stays off; if consent is later
 * withdrawn it shuts down.
 *
 * Until now it booted regardless — and on precisely the pages where the
 * consent banner was suppressed, so nobody was ever asked.
 */
const IntercomWidget = props => {
  const { isAuthenticated, currentUser, allowed, onGenerateIntercomUserHash } = props;

  // The messenger has to react to three things changing: whether we may load
  // it at all, and who (if anyone) is signed in. One keyed value covers all.
  const bootKey = allowed ? `on:${isAuthenticated ? currentUser?.id?.uuid || 'anon' : 'anon'}` : 'off';

  useReactToChange(bootKey, key => {
    // Nothing to boot, and the previous run's cleanup has already shut down
    // whatever was there. Returning nothing keeps the hook's cleanup contract.
    if (key === 'off') return undefined;

    let cancelled = false;

    const boot = async () => {
      const intercomOptions = {
        app_id: process.env.REACT_APP_INTERCOM_WIDGET_ID,
        hide_default_launcher: false,
      };

      if (isAuthenticated && currentUser) {
        intercomOptions.user_id = currentUser.id.uuid;
        intercomOptions.name = currentUser.attributes.profile.displayName;
        intercomOptions.email = currentUser.attributes.email;
        intercomOptions.created_at = Math.floor(
          new Date(currentUser.attributes.createdAt).getTime() / 1000
        );
        intercomOptions.user_hash = await onGenerateIntercomUserHash(currentUser.id.uuid);
      }

      // Consent may have been withdrawn while the hash was in flight.
      if (!cancelled) Intercom(intercomOptions);
    };

    boot();

    // Runs when the key changes (consent withdrawn, user signed out or
    // switched) and on unmount. This is what actually honours a withdrawal.
    return () => {
      cancelled = true;
      update({ hide_default_launcher: true });
      shutdown();
    };
  });

  return null;
};

IntercomWidget.defaultProps = {
  isAuthenticated: false,
  currentUser: null,
  allowed: false,
};

IntercomWidget.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  allowed: bool,
  consent: object,
  onGenerateIntercomUserHash: func.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.Auth;
  const { currentUser } = state.user;
  const { consent } = state.cookieConsent;
  return {
    isAuthenticated,
    currentUser,
    allowed: hasConsent(consent, CONSENT_CATEGORIES.FUNCTIONAL),
  };
};

const mapDispatchToProps = dispatch => ({
  onGenerateIntercomUserHash: userId => dispatch(generateIntercomUserHash(userId)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(IntercomWidget);
